import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, OnDestroy, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsEnums, webDtos } from '@vectronic/openApi-admin';
import { SchedulePreview, utcMoment } from '@vectronic/util';
import { combineLatest, Subscription } from 'rxjs';
import { AbstractScheduleService } from '../../services/abstract-schedule.service';
import { RemoteCommandsService } from '../../services/commands/remote-commands.service';
import { ScheduleCalendarCalculationService } from '../../services/schedule-calendar-calculation.service';
import { ScheduleServices } from '../../services/schedule.service';

@Component({
  selector: 'rc-calendar-day-view',
  templateUrl: './calendar-day-view.component.html',
  styleUrls: ['./calendar-day-view.component.scss'],
  imports: [MatIconButton, MatTooltip, MatIcon, NgTemplateOutlet, MatError, NgClass, DatePipe],
})
export class CalendarDayViewComponent implements OnDestroy {
  remoteCommandService = inject(RemoteCommandsService);
  private scheduleServices = inject(ScheduleServices);
  private calendarCalculationService = inject(ScheduleCalendarCalculationService);

  scheduleType = input<dsEnums.ScheduleType>();
  selectedDate = input<Date | null>();

  schedulePreview = signal<SchedulePreview[]>(undefined);
  rulesArray = signal<webDtos.ScheduleRule[]>(undefined);
  indexArray = signal<number[]>(undefined);

  dayChange = output<Date>();
  back = output();

  subscription: Subscription;

  scheduleService = computed<AbstractScheduleService>(() => {
    if (!this.scheduleType()) return undefined;
    return this.scheduleServices[this.scheduleType()];
  });

  constructor() {
    toObservable(this.scheduleService)
      .pipe(takeUntilDestroyed())
      .subscribe((service) => {
        this.subscription?.unsubscribe();
        if (service) {
          this.subscription = combineLatest([
            service.rules$,
            service.fixPerTransmission$,
          ]).subscribe(([rules]) => {
            this.rulesArray.set(Object.values(rules));
            this.indexArray.set(Object.keys(rules).map((i) => parseInt(i)));
            this.updateSchedulesInDay();
          });
        }
      });

    toObservable(this.selectedDate)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.updateSchedulesInDay();
      });
  }

  updateSchedulesInDay() {
    const schedules: SchedulePreview[] = [];
    const service = this.scheduleService();
    if (service) {
      this.rulesArray()?.forEach((rule, i) => {
        const index = this.indexArray()?.[i];
        schedules.push(
          ...this.calendarCalculationService.schedulesInDay(
            rule,
            utcMoment(this.selectedDate(), true),
            index,
            service.config,
            service.fixPerTransmission$.value?.[index],
          ),
        );
      });
    }
    const uniqueSchedules = new Map<number, SchedulePreview>();
    schedules.forEach((schedule) => {
      if (!uniqueSchedules.has(schedule.millis) || !schedule.isSkipped) {
        uniqueSchedules.set(schedule.millis, schedule);
      }
    });
    this.schedulePreview.set(
      Array.from(uniqueSchedules.values())?.sort((a, b) => a.millis - b.millis),
    );
  }

  millisToString(millis: number) {
    return utcMoment(millis, true).format('HH:mm');
  }

  onClickBack() {
    this.back.emit();
  }

  onClickPrevious() {
    this.dayChange.emit(utcMoment(this.selectedDate(), true).add('day', -1).toDate());
  }

  onClickNext() {
    this.dayChange.emit(utcMoment(this.selectedDate(), true).add('day', 1).toDate());
  }

  getHourTooltip(label: string, isSkipped: boolean) {
    const supportsSkipCount = this.scheduleService()?.config?.hasSkipCount !== false;
    if (this.remoteCommandService.hasNotShortHeaderFormat() && supportsSkipCount) {
      return (
        label +
        (isSkipped ? ' (Skipped)' : '') +
        'The exact skipped position depends on the time when the collar is turned on and might not be correct in the preview!'
      );
    }
    return label + (isSkipped ? ' (Skipped)' : '');
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }
}
